import { useState, useEffect } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { useAuthStore, apiClient } from '../store/authStore';
import { MagnifyingGlass, Globe, Spinner, CheckCircle, ArrowLeft, Tag } from '@phosphor-icons/react';

const C = {
  bg:'#0a0a0f', surface:'#13131f', input:'#1a1a2e',
  border:'rgba(124,58,237,0.2)', borderHi:'rgba(124,58,237,0.45)',
  text:'#e8e8f0', muted:'#9090b0', hint:'#5a5a7a',
  violet:'#7c3aed', blue:'#3b82f6',
  grad:'linear-gradient(135deg, #7c3aed 0%, #3b82f6 100%)',
  green:'#10b981', red:'#f87171',
};

const BACKEND_URL = process.env.REACT_APP_API_URL || "https://seodash360.com";

export default function NewAnalysis() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { isAuthenticated, isLoading: authLoading, user } = useAuthStore();

  const [url, setUrl] = useState('');
  const [keywords, setKeywords] = useState('');
  const [projectId, setProjectId] = useState('');
  const [analysisId, setAnalysisId] = useState(null);
  const [status, setStatus] = useState('idle');
  const [progress, setProgress] = useState(0);
  const [currentStep, setCurrentStep] = useState('');
  const [error, setError] = useState('');
  const [expertData, setExpertData] = useState(null);

  const runExpertScan = async (text) => {
    try {
      const res = await fetch(process.env.REACT_APP_EXPERT_API_URL, {
        method: "POST",
        headers: { "Content-Type": "application/json", "X-API-Key": process.env.REACT_APP_EXPERT_API_KEY },
        body: JSON.stringify({ text })
      });
      const data = await res.json();
      setExpertData(data);
    } catch (e) { console.error("Expert API Error", e); }
  };

  useEffect(() => {
    if (!authLoading && !isAuthenticated) { navigate('/login'); return; }
    const urlParam = searchParams.get('url');
    const projectParam = searchParams.get('project');
    if (urlParam) setUrl(urlParam);
    if (projectParam) setProjectId(projectParam);
  }, [isAuthenticated, authLoading, navigate, searchParams]);

  useEffect(() => {
    if (analysisId && status === 'running') {
      const interval = setInterval(async () => {
        try {
          const data = await apiClient.get(`/analyses/${analysisId}/status`);
          setProgress(data.progress || 0);
          setCurrentStep(data.step || '');
          if (data.status === 'completed') { setStatus('completed'); clearInterval(interval); }
          else if (data.status === 'failed') { setStatus('failed'); setError(data.error || 'Analysis failed'); clearInterval(interval); }
        } catch (err) { console.error('Polling error:', err); }
      }, 2000);
      return () => clearInterval(interval);
    }
  }, [analysisId, status]);

  const handleStartAnalysis = async (e) => {
    e.preventDefault();
    setError(''); setStatus('running'); setProgress(0);
    try {
      let pid = projectId;
      if (!pid) {
        const project = await apiClient.post('/projects', { name: new URL(url).hostname, url });
        pid = project.project_id;
        setProjectId(pid);
      }
      const analysis = await apiClient.post('/analyses', {
        project_id: pid, url,
        keywords: keywords.split(',').map(k => k.trim()).filter(k => k)
      });
      setAnalysisId(analysis.analysis_id);
      runExpertScan(keywords);
    } catch (err) { setStatus('failed'); setError(err.message || 'Failed to start analysis'); }
  };

  if (authLoading) return (
    <div style={{ minHeight:'100vh', background:C.bg, display:'flex', alignItems:'center', justifyContent:'center' }}>
      <Spinner style={{ width:28, height:28, color:C.violet }} className="animate-spin" />
    </div>
  );

  const inp = {
    width:'100%', height:44, paddingLeft:38, paddingRight:12,
    background:C.input, border:`1px solid ${C.border}`, borderRadius:9,
    fontSize:14, color:C.text, outline:'none', boxSizing:'border-box', fontFamily:'inherit',
  };

  return (
    <div style={{ minHeight:'100vh', background:C.bg, fontFamily:"'IBM Plex Sans', system-ui, sans-serif" }}>
      <header style={{ background:C.surface, borderBottom:`1px solid ${C.border}`, position:'sticky', top:0, zIndex:50 }}>
        <div style={{ maxWidth:1280, margin:'0 auto', padding:'0 24px', height:56, display:'flex', alignItems:'center', justifyContent:'space-between' }}>
          <Link to="/" style={{ display:'flex', alignItems:'center', gap:10, textDecoration:'none' }}>
            <div style={{ width:34, height:34, background:C.grad, borderRadius:8, display:'flex', alignItems:'center', justifyContent:'center' }}>
              <MagnifyingGlass weight="bold" style={{ width:16, height:16, color:'#fff' }} />
            </div>
            <span style={{ fontSize:17, fontWeight:800, fontFamily:"'Satoshi', sans-serif", color:C.text, letterSpacing:'-0.02em' }}>SEO Dash 360</span>
          </Link>
          <Link to="/dashboard" style={{ display:'flex', alignItems:'center', gap:6, padding:'6px 14px', background:'transparent', border:`1px solid ${C.border}`, borderRadius:7, fontSize:13, color:C.muted, textDecoration:'none' }}>
            <ArrowLeft style={{ width:13, height:13 }} /> Dashboard
          </Link>
        </div>
      </header>

      <main style={{ maxWidth:560, margin:'0 auto', padding:'48px 24px' }}>
        {status === 'idle' && (
          <div style={{ background:C.surface, border:`1px solid ${C.border}`, borderRadius:16, padding:36 }}>
            <h1 style={{ fontFamily:"'Satoshi', sans-serif", fontSize:22, fontWeight:900, color:C.text, margin:'0 0 24px', letterSpacing:'-0.02em' }}>New SEO Analysis</h1>
            <form onSubmit={handleStartAnalysis} style={{ display:'flex', flexDirection:'column', gap:20 }}>
              <div>
                <label style={{ display:'block', fontSize:13, fontWeight:600, color:C.muted, marginBottom:6 }}>Website URL</label>
                <div style={{ position:'relative' }}>
                  <Globe style={{ position:'absolute', left:12, top:'50%', transform:'translateY(-50%)', width:16, height:16, color:C.hint }} />
                  <input type="url" value={url} onChange={e=>setUrl(e.target.value)}
                    placeholder="https://example.com" required style={inp}
                    onFocus={e=>e.target.style.borderColor=C.violet}
                    onBlur={e=>e.target.style.borderColor=C.border}
                    data-testid="analysis-url-input" />
                </div>
              </div>
              <div>
                <label style={{ display:'block', fontSize:13, fontWeight:600, color:C.muted, marginBottom:6 }}>
                  Target Keywords <span style={{ color:C.hint, fontWeight:400 }}>(optional)</span>
                </label>
                <div style={{ position:'relative' }}>
                  <Tag style={{ position:'absolute', left:12, top:'50%', transform:'translateY(-50%)', width:16, height:16, color:C.hint }} />
                  <input type="text" value={keywords} onChange={e=>setKeywords(e.target.value)}
                    placeholder="seo tools, website analyzer, ..." style={inp}
                    onFocus={e=>e.target.style.borderColor=C.violet}
                    onBlur={e=>e.target.style.borderColor=C.border}
                    data-testid="analysis-keywords-input" />
                </div>
                <p style={{ fontSize:12, color:C.hint, margin:'5px 0 0' }}>Separate multiple keywords with commas</p>
              </div>

              {expertData && (
                <div style={{ padding:16, background:'rgba(124,58,237,0.06)', border:`1px solid ${C.border}`, borderRadius:12 }}>
                  <div style={{ display:'flex', alignItems:'center', gap:8, marginBottom:12 }}>
                    <Tag weight="bold" style={{ color:C.violet }} />
                    <span style={{ fontWeight:700, color:C.text, fontSize:14 }}>Expert Semantic Insights</span>
                  </div>
                  <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:10 }}>
                    <div style={{ padding:12, background:C.input, borderRadius:8 }}>
                      <p style={{ fontSize:11, color:C.hint, margin:'0 0 4px' }}>Richesse Lexicale</p>
                      <p style={{ fontSize:18, fontWeight:900, color:C.violet, margin:0 }}>{Math.round(expertData.metrics.lexical_richness * 100)}%</p>
                    </div>
                    <div style={{ padding:12, background:C.input, borderRadius:8 }}>
                      <p style={{ fontSize:11, color:C.hint, margin:'0 0 4px' }}>Risque Optimisation</p>
                      <p style={{ fontSize:18, fontWeight:900, color:expertData.metrics.overoptimization_risk==='High'?C.red:C.green, margin:0 }}>
                        {expertData.metrics.overoptimization_risk}
                      </p>
                    </div>
                  </div>
                </div>
              )}

              {error && (
                <div style={{ padding:14, background:'rgba(248,113,113,0.1)', border:'1px solid rgba(248,113,113,0.3)', borderRadius:8, color:C.red, fontSize:13 }} data-testid="analysis-error">
                  {error}
                </div>
              )}

              <button type="submit"
                style={{ width:'100%', height:46, background:C.grad, border:'none', borderRadius:10, fontSize:15, fontWeight:700, color:'#fff', cursor:'pointer', display:'flex', alignItems:'center', justifyContent:'center', gap:8, boxShadow:'0 4px 20px rgba(124,58,237,0.3)', fontFamily:'inherit' }}
                data-testid="start-analysis-btn">
                <MagnifyingGlass style={{ width:18, height:18 }} />
                Start Analysis
              </button>
            </form>

            {user?.plan === 'free' && (
              <div style={{ marginTop:20, padding:14, background:'rgba(124,58,237,0.06)', border:`1px solid ${C.border}`, borderRadius:8 }}>
                <p style={{ fontSize:12, color:C.muted, margin:0 }}>
                  <strong style={{ color:C.text }}>Free plan:</strong> 1 analysis per day.{' '}
                  <Link to="/pricing" style={{ color:C.violet }}>Upgrade for unlimited</Link>
                </p>
              </div>
            )}
          </div>
        )}

        {(status === 'running' || status === 'completed' || status === 'failed') && (
          <div style={{ background:C.surface, border:`1px solid ${C.border}`, borderRadius:16, padding:40, textAlign:'center' }}>
            {status === 'running' && (
              <>
                <div style={{ width:88, height:88, margin:'0 auto 24px', position:'relative' }}>
                  <svg style={{ width:'100%', height:'100%', transform:'rotate(-90deg)' }} viewBox="0 0 100 100">
                    <circle cx="50" cy="50" r="45" fill="none" stroke="rgba(124,58,237,0.15)" strokeWidth="8" />
                    <circle cx="50" cy="50" r="45" fill="none" stroke={C.violet} strokeWidth="8"
                      strokeLinecap="round" strokeDasharray={`${progress * 2.83} 283`}
                      style={{ transition:'stroke-dasharray 0.5s ease', filter:'drop-shadow(0 0 6px rgba(124,58,237,0.6))' }} />
                  </svg>
                  <div style={{ position:'absolute', inset:0, display:'flex', alignItems:'center', justifyContent:'center' }}>
                    <span style={{ fontSize:22, fontWeight:900, color:C.violet, fontFamily:"'JetBrains Mono', monospace" }}>{progress}%</span>
                  </div>
                </div>
                <h2 style={{ fontFamily:"'Satoshi', sans-serif", fontSize:19, fontWeight:800, color:C.text, margin:'0 0 8px' }}>Analyzing your website...</h2>
                <p style={{ color:C.muted, margin:'0 0 20px', fontSize:14 }}>{currentStep || 'Initializing...'}</p>
                <div style={{ background:C.input, borderRadius:8, padding:'10px 14px' }}>
                  <code style={{ fontSize:12, color:C.hint, fontFamily:"'JetBrains Mono', monospace" }}>{url}</code>
                </div>
              </>
            )}

            {status === 'completed' && (
              <>
                <div style={{ width:72, height:72, borderRadius:'50%', background:'rgba(16,185,129,0.12)', display:'flex', alignItems:'center', justifyContent:'center', margin:'0 auto 20px' }}>
                  <CheckCircle weight="fill" style={{ width:40, height:40, color:C.green }} />
                </div>
                <h2 style={{ fontFamily:"'Satoshi', sans-serif", fontSize:19, fontWeight:800, color:C.text, margin:'0 0 8px' }}>Analysis Complete!</h2>
                <p style={{ color:C.muted, margin:'0 0 24px', fontSize:14 }}>Your SEO report is ready to view.</p>
                <button onClick={() => navigate(`/report/${analysisId}`)}
                  style={{ padding:'12px 32px', background:C.grad, border:'none', borderRadius:10, fontSize:15, fontWeight:700, color:'#fff', cursor:'pointer', boxShadow:'0 4px 20px rgba(124,58,237,0.3)', fontFamily:'inherit' }}
                  data-testid="view-report-btn">
                  View Report
                </button>
              </>
            )}

            {status === 'failed' && (
              <>
                <div style={{ width:72, height:72, borderRadius:'50%', background:'rgba(248,113,113,0.12)', display:'flex', alignItems:'center', justifyContent:'center', margin:'0 auto 20px' }}>
                  <span style={{ fontSize:36 }}>⚠</span>
                </div>
                <h2 style={{ fontFamily:"'Satoshi', sans-serif", fontSize:19, fontWeight:800, color:C.text, margin:'0 0 8px' }}>Analysis Failed</h2>
                <p style={{ color:C.red, margin:'0 0 24px', fontSize:14 }}>{error}</p>
                <button onClick={() => { setStatus('idle'); setError(''); }}
                  style={{ padding:'12px 32px', background:C.grad, border:'none', borderRadius:10, fontSize:15, fontWeight:700, color:'#fff', cursor:'pointer', boxShadow:'0 4px 20px rgba(124,58,237,0.3)', fontFamily:'inherit' }}
                  data-testid="try-again-btn">
                  Try Again
                </button>
              </>
            )}
          </div>
        )}
      </main>
    </div>
  );
}
